import { Autocomplete, TextField } from "@mui/material"
import { useEffect, useMemo, useState, type JSX } from "react"

import { customersApi } from "@crm-src/lib/customers-api"

export interface CustomerPick {
  id: string
  name: string
}

export interface CustomerSearchPickerProps {
  value: CustomerPick | null
  onChange: (next: CustomerPick | null) => void
  label?: string
  error?: boolean
  disabled?: boolean
}

/** How long after the last keystroke the customers endpoint is asked. */
export const CUSTOMER_SEARCH_DEBOUNCE_MS = 250

/**
 * A server-searched customer picker — the same light request the licence
 * dialog and the invoice generator make (`GET /customers?search=…&
 * customer_type=Customer&per_page=20&include_deals=false`), so a tenant with
 * thousands of customers is searched rather than scrolled. crm-api scopes the
 * rows to the viewer, which is also what the purchase-order endpoint checks.
 */
export function CustomerSearchPicker({
  value,
  onChange,
  label = "Customer",
  error = false,
  disabled = false,
}: CustomerSearchPickerProps): JSX.Element {
  const [query, setQuery] = useState("")
  const [options, setOptions] = useState<CustomerPick[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    let cancelled = false
    const handle = setTimeout(async () => {
      setSearching(true)
      try {
        const term = query.trim()
        const response = await customersApi.getAll({
          ...(term ? { search: term } : {}),
          customer_type: "Customer",
          per_page: 20,
          include_deals: false,
        } as any)
        if (cancelled) return
        setOptions(
          (response.customers ?? []).map((row: any) => ({
            id: String(row.id),
            name: row.company_name || row.full_name || row.name || `Customer ${row.id}`,
          })),
        )
      } catch {
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, CUSTOMER_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [query])

  // The picked customer must be IN the options for MUI to show it selected,
  // even when the current search no longer returns it (an edit, say).
  const withValue = useMemo(
    () => (value && !options.some((o) => o.id === value.id) ? [value, ...options] : options),
    [options, value],
  )

  return (
    <Autocomplete<CustomerPick, false, false, false>
      size="small"
      options={withValue}
      value={value}
      loading={searching}
      disabled={disabled}
      data-cy="po-customer-picker"
      onChange={(_event, next) => {
        onChange(next)
        setQuery("")
      }}
      onInputChange={(_event, next, reason) => {
        // "reset" is MUI writing the picked name into the box, not a search.
        if (reason === "reset") return
        setQuery(next)
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(all) => all}
      noOptionsText={query.trim() ? "No customer matches." : "Type a company name."}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Search customers" error={error} />
      )}
    />
  )
}
